// The block shapes ("pieces") that fall into the drawer.
//
// NOTE: this is a faithful PROVISIONAL set authored from the original shape names and the
// in-game screenshots so the game is fully playable now. A background tool extracts the exact
// cell geometry from the Unity prefabs into `shared/shapes.json`; once present, `loadShapes()`
// prefers it. Each shape's per-cube value is derived as floor(cellCount / 2), min 1
// (matching Unity `CubeHolder.Init`: pointVal = cubes.Count / 2).

export interface Cell {
  x: number;
  z: number;
}

export interface ShapeDef {
  name: string;
  /** Relative spawn weight, 0..100 (Unity SO `chance`). Higher = more common. */
  chance: number;
  /** Normalized cells (min x = 0, min z = 0). */
  cells: Cell[];
}

// The real shape geometry extracted from the Unity prefabs (tools/extract_shapes.mjs).
import shapesJson from '@shared/shapes.json';

const c = (...pairs: [number, number][]): Cell[] => pairs.map(([x, z]) => ({ x, z }));

export const FALLBACK_SHAPES: ShapeDef[] = [
  { name: 'Double', chance: 100, cells: c([0, 0], [1, 0]) },
  { name: 'Line', chance: 100, cells: c([0, 0], [1, 0], [2, 0], [3, 0]) },
  { name: 'SmallL', chance: 100, cells: c([0, 0], [0, 1], [1, 1]) },
  { name: 'BigL', chance: 100, cells: c([0, 0], [0, 1], [0, 2], [1, 2]) },
  { name: 'MegaL', chance: 80, cells: c([0, 0], [0, 1], [0, 2], [1, 2], [2, 2]) },
  { name: 'SmallBoy', chance: 100, cells: c([0, 0], [1, 0], [0, 1], [1, 1]) },
  { name: 'BiggerBoy', chance: 100, cells: c([0, 0], [1, 0], [0, 1], [1, 1], [0, 2], [1, 2]) },
  {
    name: 'TalBiggerBoy',
    chance: 100,
    cells: c([0, 0], [1, 0], [0, 1], [1, 1], [0, 2], [1, 2], [0, 3], [1, 3]),
  },
  {
    name: 'GigaChad',
    chance: 25,
    cells: c([0, 0], [1, 0], [2, 0], [0, 1], [1, 1], [2, 1], [0, 2], [1, 2], [2, 2]),
  },
  { name: 'Cross', chance: 40, cells: c([1, 0], [0, 1], [1, 1], [2, 1], [1, 2]) },
  { name: 'T', chance: 20, cells: c([0, 0], [1, 0], [2, 0], [1, 1], [1, 2]) },
  { name: 'W', chance: 30, cells: c([0, 0], [0, 1], [1, 1], [1, 2], [2, 2]) },
  { name: 'TheZ', chance: 10, cells: c([1, 0], [2, 0], [0, 1], [1, 1]) },
  { name: 'TheZ2', chance: 10, cells: c([0, 0], [1, 0], [1, 1], [2, 1]) },
  { name: 'Bridge', chance: 10, cells: c([0, 0], [2, 0], [0, 1], [1, 1], [2, 1]) },
  { name: 'SpaceInvader', chance: 100, cells: c([0, 0], [2, 0], [0, 1], [1, 1], [2, 1], [1, 2]) },
  { name: 'ThumbsUp', chance: 20, cells: c([0, 1], [1, 1], [2, 0], [2, 1], [2, 2]) },
  { name: 'ThumbsUp2', chance: 20, cells: c([0, 0], [0, 1], [0, 2], [1, 1], [2, 1]) },
  { name: 'Huh', chance: 10, cells: c([0, 0], [1, 0], [1, 1], [1, 2], [2, 2]) },
  { name: 'Huh2', chance: 10, cells: c([1, 0], [2, 0], [1, 1], [0, 1], [0, 2]) },
  { name: 'Footsteps', chance: 100, cells: c([0, 0], [1, 0], [1, 1], [2, 1], [2, 2], [3, 2]) },
  { name: 'Footsteps2', chance: 100, cells: c([3, 0], [2, 0], [2, 1], [1, 1], [1, 2], [0, 2]) },
];

/** The shapes actually used by the game: real prefab geometry, falling back to the authored set. */
export const SHAPES: ShapeDef[] =
  Array.isArray(shapesJson) && shapesJson.length ? (shapesJson as ShapeDef[]) : FALLBACK_SHAPES;

/** Per-cube value for a piece, faithful to Unity `CubeHolder.Init`. */
export function shapeValue(cellCount: number): number {
  return Math.max(1, Math.floor(cellCount / 2));
}

/** Normalize a set of cells so the minimum x and z are 0. */
export function normalizeCells(cells: Cell[]): Cell[] {
  const minX = Math.min(...cells.map((p) => p.x));
  const minZ = Math.min(...cells.map((p) => p.z));
  return cells.map((p) => ({ x: p.x - minX, z: p.z - minZ }));
}

/** Rotate cells 90° clockwise `steps` times (steps mod 4), then normalize. */
export function rotateCells(cells: Cell[], steps: number): Cell[] {
  const s = ((steps % 4) + 4) % 4;
  let out = cells.map((p) => ({ ...p }));
  for (let i = 0; i < s; i++) {
    // 90° CW: (x, z) -> (z, -x)  [normalize afterwards]
    out = out.map((p) => ({ x: p.z, z: -p.x }));
    out = normalizeCells(out);
  }
  return normalizeCells(out);
}

/** Bounding box width/height of a cell set. */
export function cellsExtent(cells: Cell[]): { w: number; h: number } {
  return {
    w: Math.max(...cells.map((p) => p.x)) + 1,
    h: Math.max(...cells.map((p) => p.z)) + 1,
  };
}
